/****************************************************************************************
 *  theme.service.ts                                                                    *
 *  ----------------------------------------------------------------------------------  *
 *  ▸ Keeps backward-compatible setters/getters for existing code.                      *
 *  ▸ Exposes a **reactive** `theme$` Observable that updates when LaunchDarkly pushes  *
 *    a new theme variation.                                                            *
 ****************************************************************************************/

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { LaunchDarklyService, LDThemeCampaign } from '@core/services/launchdarkly.service';
import { NetflixWednesdayActionsService } from './theme-actions/netflix-wednesday-actions.service';
import { ThemeActionsService } from './theme-actions/theme-actions.service';
import { LDTheme } from '@core/services/launchdarkly.service';
import { ThinMintActionsService } from './theme-actions/thin-mint-actions.service';
import { MinionsActionsService } from './theme-actions/minions-actions.service';

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {
  /* ------------------------------------------------------------------ */
  /*  Internal state                                                     */
  /* ------------------------------------------------------------------ */

  /** Live cache of the latest theme (keeps legacy API working). */
  private readonly themeSubject = new BehaviorSubject<LDThemeCampaign[] | undefined>(undefined);

  /** Map of theme-class → concrete action implementation. */
  private readonly knownThemeActions = new Map<string, ThemeActionsService>();

  /** Subscription to LaunchDarkly stream (cleaned up on destroy). */
  private ldSub?: Subscription;

  /* ------------------------------------------------------------------ */
  /*  Construction & reactive wiring                                     */
  /* ------------------------------------------------------------------ */

  constructor(
    ld: LaunchDarklyService, // unified service with currentTheme$()
    netflixWednesday: NetflixWednesdayActionsService,
    thinMint: ThinMintActionsService,
    minions: MinionsActionsService,
  ) {
    /* register action handlers */
    this.knownThemeActions.set('netflix-wednesday', netflixWednesday);
    this.knownThemeActions.set('thin-mint', thinMint);
    this.knownThemeActions.set('minions', minions);

    /* subscribe to LD theme stream and push updates → BehaviorSubject */
    this.ldSub = ld
      .currentThemeCampaigns$() // Observable<LDTheme>
      .pipe(distinctUntilChanged()) // skip duplicates
      .subscribe((t) => this.themeSubject.next(t));
  }

  ngOnDestroy(): void {
    this.ldSub?.unsubscribe();
  }

  /* ------------------------------------------------------------------ */
  /*  Backward-compatible imperative API                                 */
  /* ------------------------------------------------------------------ */

  /** (Legacy) get the current cached theme synchronously. */
  public getThemeCampaigns(campaign: string, daypart: string): LDTheme | undefined {
    return this.themeSubject.value?.find((c) => c.id === campaign)?.dayparts.find((d) => d.id === daypart)?.content;
  }

  /**
   * Returns a concrete **ThemeActionsService** for the active theme,
   * or `undefined` when no action handler is registered.
   */
  public getThemeActionService(campaign: string, daypart: string): ThemeActionsService | undefined {
    const klass = this.themeSubject.value?.find((c) => c.id === campaign)?.dayparts.find((d) => d.id === daypart)
      ?.content.class;
    return klass ? this.knownThemeActions.get(klass) : undefined;
  }

  /* ------------------------------------------------------------------ */
  /*  New reactive API                                                   */
  /* ------------------------------------------------------------------ */

  /** Observable stream that emits whenever the LaunchDarkly theme changes. */
  public theme$(campaign: string, daypart: string): Observable<LDTheme | undefined> {
    return this.themeSubject
      .asObservable()
      .pipe(map((themes) => themes?.find((c) => c.id === campaign)?.dayparts.find((d) => d.id === daypart)?.content));
  }
}
